import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox, Spinner } from "src/components";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { generateUniqueID } from "src/constants/functions";
import { ordActions } from "src/context/order-slice";
import { RootState } from "src/context/store";
import usePermission from "src/hooks/usePermission";
import { useToast } from "src/hooks/useToast";
import { addCounterStockist, getNongstMtdLimit, getSignedUrl, getStockist, postPlaceOrder, uploadFileToPresignedUrl, getHqValueValidation } from "src/services/order-api";
import useOrderUinConfirmation from "../../../../hooks/useOrderUinConfirmation";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import Button from "../../components/Button";
import OrderReviewProductCard from "./OrderReviewProductCard";
import SuccessIcon from "../../assets/successCircle.svg";
import useDebounceLeading from "src/hooks/useDebounceLeading";
import useIsMounted from "src/hooks/useIsMounted";  //M10-15110
import FormSuccess from "../../container/FormSuccess";

interface Item {
  companyProducts?: {
    item_division: {
      cluster_code: {
        cluster_code: string;
        cluster_name: string;
      };
    }[];
  };
}

const OrderReviewScreen = () => {
  const { toast } = useToast();

  const { locationCode } = useParams();
  const { cartDataObj, selectedCounterData } = useSelector((state: RootState) => state.ord);
  const { idTokenDetails } = useSelector((state: RootState) => state.auth);
  const { currentPsr } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const isMountedRef = useIsMounted();  //M10-15110
  const [remark, setRemark] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);
  const [stockist, setStockist] = useState<any>(null);
  const [selectedStockist, setSelectedStockist] = useState<any[]>([]);
  const [isLoading] = useState(false);
  const [orderType, setOrderType] = useState<any>("10");
  const [clusterCodes, setClusterCodes] = useState<any[]>([]);
  const [file, setFile] = useState<File | null>(null); // State to store the selected file
  const [stockistFile, setStockistFile] = useState<File | null>(null); // State to store the selected file
  const [uploading, setUploading] = useState(false); // State to handle loading state
  const [uploaded, setUploaded] = useState(false);
  const [districtStockist, setDistrictStockist] = useState<any[]>([]);
  const [nonDefaultCounterStocist, setNonDefaultCounterStocist] = useState<any[]>([]);
  const [clusterDivision, setClusterDivision] = useState<any[]>([]);
  const [filePath, setFilePath] = useState<{
    counter_copy_path: string | "";
    stockist_copy_path: string | "";
  }>({
    counter_copy_path: "",
    stockist_copy_path: "",
  });
  const gstAvailable = selectedCounterData?.counter_location?.counter_hdr_id?.gst_available === "Yes";
  const [isNonGSTOrderLimitModalOpen, setIsNonGSTOrderLimitModalOpen] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [gstLimitExceededMessage, setGstLimitExceededMessage] = useState<string>("");
  const [divisionsData, setDivisionsData] = useState<any[]>([]);
  const [skipNotifications, setSkipNotifications] = useState(false)

  const { hasPermission: hasSkipNotificationsPermission } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.SKIPNOTIFICATIONS.module,
    action: fineGrainedPermissionsKeys.ORDER.SKIPNOTIFICATIONS.action,
  });

  // M10-16361: Order Placed Date (order_complete_date) - needs RBAC permission and "MV MO without Approval" order mode
  const { hasPermission: hasOrderPlacedDatePermission } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.ORDERPLACEDDATE.module,
    action: fineGrainedPermissionsKeys.ORDER.ORDERPLACEDDATE.action,
  });
  const canSetOrderCompleteDate = hasOrderPlacedDatePermission && orderType === "11";

  const [selectedStockistMap, setSelectedStockistMap] = useState<Record<string, {}>>({});
  const [hqValueValidationModalOpen, setHqValueValidationModalOpen]=useState(false);
  const [hqValueValidationRes, setHqValueValidationRes]=useState<any>(null)


  const isSalesRequired = window.sessionStorage.getItem("iSalesRequired") === "true";

  const dispatch = useDispatch();

  const {
    modal,
    isOpen: checkingUINOpen,
    checkUINValidation,
    isLoading: checkingUINLoading,
  } = useOrderUinConfirmation({
    orderCode: "",
    isApproval: false,
    divisionCode: (() => {
      const uniqueDivisionsMap = new Map();

      Object.values(cartDataObj).forEach((item: any) => {
        const counterDivisionCode = item?.counter_ff_div_id?.company_division_code?.company_divisioncode;
        const counter_ff_div_lnk = item?.counter_ff_div_id?.id;
        if (counterDivisionCode && !uniqueDivisionsMap.has(counterDivisionCode)) {
          uniqueDivisionsMap.set(counterDivisionCode, { counterDivisionCode, counter_ff_div_lnk, item });
        }
      });

      return Array.from(uniqueDivisionsMap.values());
    })(),
    divisionProductsData: Object.values(cartDataObj),
    counterCode: selectedCounterData?.counter_location?.counter_hdr_id?.counter_code,
    counterLocationCode: selectedCounterData?.counter_location?.location_code,
    counterDivData: {
      PSR_UniqueId: selectedCounterData?.counter_field_force_id?.[0]?.employee_details?.psr_ucode,
      Position_Code_Name: selectedCounterData?.counter_field_force_id?.[0]?.employee_details?.position_code_name,     //needs to be mapped later or never
      ass_counter_name: selectedCounterData?.counter_field_force_id?.[0]?.associated_counter_name,     //needs to be mapped later or never
      ass_contact_number: selectedCounterData?.counter_field_force_id?.[0]?.associated_contact_number,   //needs to be mapped later or never
      uin_code: selectedCounterData?.counter_field_force_id?.[0]?.associated_uin_code,   //needs to be mapped later or never
      FF_Name: selectedCounterData?.counter_field_force_id?.[0]?.employee_details?.emp_name,
      FF_ShortCode: selectedCounterData?.counter_field_force_id?.[0]?.employee_details?.emp_shortcode,
    },
    onUinVerified: () => {
      handlePlaceOrder();
    },
  });

  const handleRemarkChange = (e: any) => {
    setRemark(e.target.value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
  };

  const handleStockistFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setStockistFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file && !stockistFile) {
      toast({ description: "Please select a file before uploading.", variant: "destructive" });
      return;
    }

    setUploading(true);
    const uploadKeys: string[] = [];
    const fileUploadUrls: { counter: string; stockist: string } = { counter: "", stockist: "" };

    if (file) uploadKeys.push("counter_path");
    if (stockistFile) uploadKeys.push("stockist_path");

    if (uploadKeys.length > 0) {
      const signedRes = await getSignedUrl({ fileNames: uploadKeys });
      const presignedUrls = signedRes?.data?.data?.presignedUrls || {};

      const newFilePath = { ...filePath };

      if (file && presignedUrls.counter_path) {
        newFilePath.counter_copy_path = presignedUrls.counter_path.s3Key;
        fileUploadUrls.counter = presignedUrls.counter_path.uploadUrl;
      }

      if (stockistFile && presignedUrls.stockist_path) {
        newFilePath.stockist_copy_path = presignedUrls.stockist_path.s3Key;
        fileUploadUrls.stockist = presignedUrls.stockist_path.uploadUrl;
      }

      setFilePath(newFilePath);
    }

    try {
      if (file) {
        await uploadFileToPresignedUrl(file, fileUploadUrls?.counter);
        setUploaded(true);
      }
      if (stockistFile) {
        await uploadFileToPresignedUrl(stockistFile, fileUploadUrls?.stockist);
      }
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || "Failed to upload file. Please try again.",
        variant: "destructive",
      });
      console.log(error);
    } finally {
      setUploading(false);
    }
  };

  const handlePlaceOrderButtonClick = async (isNonGSTOrderLimitPass: boolean, isHqValueExceedPass: boolean) => {
    if (uploading) {
      toast({ description: "Please wait for file upload to complete before placing the order.", variant: "destructive" });
      return;
    }
    setIsPlacingOrder(true);
    try {
      if (!gstAvailable && !isNonGSTOrderLimitPass) {
        try {
          const nongstMtdLimit = await getNongstMtdLimit({
            counter_company_location_id: selectedCounterData?.counter_company_location_id,
            company_code: selectedCounterData?.company_code?.code,
            // current_order_value: subtotal,
            division_codes: divisionsData?.map((item: any) => item?.division_code)?.join(","),
            order_values: divisionsData?.map((item: any) => item?.orderValue)?.join(","),
          });
          const gstLimitsForDivisions = nongstMtdLimit?.data?.data?.data?.divisions;

          const updatedDivisionsData = divisionsData.map((item) => {
            const extraData = gstLimitsForDivisions[item.division_code];
            return {
              ...item,
              ...extraData,
            };
          });

          setDivisionsData(updatedDivisionsData);
          const hasExceeded = Object.values(updatedDivisionsData || {}).some((division: any) => division?.is_limit_exceeded);
          if (hasExceeded) {
            const message = Object.values(updatedDivisionsData || {})
              .filter((division: any) => division.is_limit_exceeded)
              .map(
                (division: any) =>
                  `For division "${division?.division_name}" the limit is ${division?.limit_value}, remaining limit is ${division?.remaining_limit}, and current order used value is ${division?.order_value}.`
              )
              .join("\n");
            setGstLimitExceededMessage(message);
            setIsNonGSTOrderLimitModalOpen(true);
            setIsPlacingOrder(false);
            return;
          } else {
            handlePlaceOrderButtonClick(true, false);
          }
        } catch (error: any) {
          toast({
            description: error?.response?.data?.message || "Failed to validate non-GST order limit. Please try again.",
            variant: "destructive",
          });
          console.log(error);
        }
      } else if (!isHqValueExceedPass) {
        handleHqValueValidation()
      }
      else {
        checkUINValidation();
      }
    } catch (err) {
      console.error("Error in handlePlaceOrderButtonClick:", err);
      toast({
        description: "An unexpected error occurred while placing the order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleHqValueValidation = async() => {
    const companyDivisionCodes = Array.from(
      new Set(
        Object.values(cartDataObj ?? {})
          .map((entry: any) => entry?.counter_ff_div_id?.company_division_code?.company_divisioncode)
          .filter((code): code is string | number => code !== undefined && code !== null && code !== "")
      )
    ).join(",");
    const quantity=Array.from(
      new Set(
        Object.values(cartDataObj ?? {})
          .map((entry: any) => entry?.cartQty)
          .filter((code): code is string | number => code !== undefined && code !== null && code !== "")
      )
    ).join(",");
    const counterProductCode = Object.keys(cartDataObj ?? {}).join(",");
    const bill_amount = subtotal
    try {
      const response = await getHqValueValidation({ company_division_code: companyDivisionCodes, counter_product_code: counterProductCode, bill_amount: bill_amount, quantity: quantity });
      if(response?.status===200 && response?.data?.success===true){
         handlePlaceOrderButtonClick(true, true)
      }
    } catch (error: any) {
      console.log(error)
      setHqValueValidationModalOpen(true);
      setHqValueValidationRes(error?.response?.data)
    }
  }


  function buildHqValidationDescriptionString(hqValueValidationRes?: any): string {
    if (hqValueValidationRes?.message) {
      return `${hqValueValidationRes?.message}.\n\nDo you still want to proceed with placing the order?`;
    }
    return "Value limit has been exceeded. Do you still want to proceed with placing the order?";
  }


  const handleStockistChange = (value: any, type: string) => {
    const valueArray = Array.isArray(value) ? value : [value];
    switch (type) {
      case "add": {
        setSelectedStockist((prev: any[]) => {
          const newSelection = valueArray.filter(
            (item: any) => !prev.some((prevItem: any) => prevItem.unique_stockist_id == item.unique_stockist_id)
          );
          return [...prev, ...newSelection];
        });

        const selectedStockistIds: string[] = [];
        const selectedStockistClusterCodes: string[] = [];
        valueArray.forEach((item: any) => {
          selectedStockistIds.push(item?.unique_stockist_id);
          selectedStockistClusterCodes.push(item?.cluster_code);
        });

        //  Disable other stockists in the same cluster
        setStockist((prev: any) =>
          prev.map((item: any) => {
            if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
              item.disabled = true;
            }
            return item;
          })
        );

        //  Disable other district stockists in the same cluster
        setDistrictStockist((prev: any) =>
          prev.map((item: any) => {
            if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
              item.disabled = true;
            }
            return item;
          })
        );

        // Disable other counter stockists in same cluster
        setNonDefaultCounterStocist((prev: any) =>
          prev.map((item: any) => {
            if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
              item.disabled = true;
            }
            return item;
          })
        );

        break;
      }

      case "remove": {
        setSelectedStockist((prev: any[]) =>
          prev.filter((item) => !valueArray.some((removeItem: any) => removeItem.unique_stockist_id == item.unique_stockist_id))
        );

        const selectedStockistIds: string[] = [];
        const selectedStockistClusterCodes: string[] = [];
        valueArray.forEach((item: any) => {
          selectedStockistIds.push(item?.unique_stockist_id);
          selectedStockistClusterCodes.push(item?.cluster_code);
        });
        //  Re-enable all stockists in the same cluster
        setStockist((prev: any) =>
          prev.map((item: any) => {
            if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
              item.disabled = false;
            }
            return item;
          })
        );

        //  Re-enable all district stockists in the same cluster
        setDistrictStockist((prev: any) =>
          prev.map((item: any) => {
            if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
              item.disabled = false;
            }
            return item;
          })
        );

        // Re-enable counter stockists in same cluster
        setNonDefaultCounterStocist((prev: any) =>
          prev.map((item: any) => {
            if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
              item.disabled = false;
            }
            return item;
          })
        );

        break;
      }
    }
  };

  const handleClusterDivisionStockistChange = (value: any, type: string, clusterDivisionKey: string) => {
    const selected = Array.isArray(value) ? value[0] : value; // still safeguard

    setSelectedStockistMap((prev) => {
      if (type === "add") {
        // ✅ Disable other stockists in this cluster
        setStockist((prevStockistList: any[]) => {
          return prevStockistList.map((item: any) => {
            if (item?.cluster_code === clusterDivisionKey.split("-")[0]) {
              item.disabled = item.unique_stockist_id !== selected.unique_stockist_id;
            }
            return item;
          });
        });

        return {
          ...prev,
          [clusterDivisionKey]: selected, // ⬅ single object now
        };
      } else if (type === "remove") {
        // ✅ Re-enable all stockists in this cluster
        setStockist((prevStockistList: any[]) => {
          return prevStockistList.map((item: any) => {
            if (item?.cluster_code === clusterDivisionKey.split("-")[0]) {
              item.disabled = false;
            }
            return item;
          });
        });

        const updated = { ...prev };
        delete updated[clusterDivisionKey];
        return updated;
      }

      return prev;
    });
  };

  // const subtotal = Object.values(cartDataObj)?.reduce((acc: number, item: any) => acc + Number(item?.companyProducts?.ptr) * item?.cartQty, 0);
  const subtotal = Object.values(cartDataObj)?.reduce((acc: number, item: any) => {
    const price =
      item?.priceldentifier?.priceldentifier_id === 3
        ? Number(item?.counter_product_slab?.[0]?.fixedprice_cp)
        : Number(item?.counter_product_slab?.[0]?.price);

    return acc + price * (item?.cartQty || 0);
  }, 0);
  const totalItems = Object.values(cartDataObj)?.reduce((acc: number, item: any) => acc + Number(item?.cartQty || 0), 0);

  const getTotalDiscount = (): number => {
    let totalDiscount = 0;

    Object.values(cartDataObj)?.forEach((item: any) => {
      const ptr = Number(item?.companyProducts?.ptr || 0);
      const cartQty = Number(item?.cartQty || 0);
      const priceIdentifier = item?.priceldentifier?.priceldentifier_id;
      const slab = item?.counter_product_slab?.[0];

      if (!slab) return;

      switch (priceIdentifier) {
        case 2: // Scheme Discount
          // totalDiscount += ptr * Math.floor(cartQty / (slab.schemeqty_cp || 1)) * (slab.quantity_cp || 0);
          totalDiscount = 0;
          break;

        case 3: // Fixed Price Discount
          totalDiscount += Number(slab.fixedprice_cp || 0) * cartQty;
          break;

        case 1: // Percentage Discount
        default:
          totalDiscount += (ptr * cartQty * Number(slab.discountonptr_cp || 0)) / 100;
          break;
      }
    });

    return Number(totalDiscount.toFixed(2));
  };

  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC +5:30
  const istTime = new Date(now.getTime() + istOffset);
  const istISOString = istTime.toISOString().slice(0, -1); // Removes the 'Z' to avoid confusion
  // M10-16361: order placed date - today or backdated days (IST) allowed; optional.
  // Sent with the current IST time of placing the order. Empty -> order initiated date (created_date).
  const todayIST = istISOString.slice(0, 10); // YYYY-MM-DD
  const [orderedDate, setOrderedDate] = useState<string>("");

  const isRequestInProgress = useRef(false);
  const handlePlaceOrder = useDebounceLeading(async () => {
    const cartData = Object.values(cartDataObj);
    const isValid = cartData.every((item: any) => {
      return Number(item?.cartQty) >= Number(item?.counter_product_slab?.[0]?.min_order_qty) && Number(item?.cartQty) <= Number(item?.counter_product_slab?.[0]?.max_order_qty);
    });
    if (!isValid) {
      toast({
        description: "Please enter a valid quantity",
        variant: "destructive",
      });
      return;
    }
    if (isRequestInProgress.current) return;
    if (currentPsr?.emp_position_hdr?.position_code == "" || currentPsr?.emp_position_hdr?.position_code == null) {
      toast({ description: "Position code is not found, Try to login again", variant: "destructive" });
      return;
    }
    if (!selectedStockist) {
      toast({ description: "Please select a stockist!", variant: "destructive" });
      return;
    }
    if (Object.keys(cartDataObj)?.length === 0) {
      toast({ description: "Please add products to the cart!", variant: "destructive" });
      return;
    }
    if (!orderType) {
      toast({ description: "Please select an order mode!", variant: "destructive" });
      return;
    }

    if ((file || stockistFile) && !uploaded) {
      toast({ description: "Please upload the file before placing the order!", variant: "destructive" });
      return;
    }

    dispatch(ordActions.setOrderStockists(selectedStockist));
    const items: Item[] = Object.values(cartDataObj);

    // Check if all clusters have a selected stockist
    if (!isSalesRequired) {
      for (const item of items) {
        const cluster_code = item?.companyProducts?.item_division[0]?.cluster_code?.cluster_code;
        const cluster_name = item?.companyProducts?.item_division[0]?.cluster_code?.cluster_name;
        const stockistForCluster = selectedStockist.find((stockist: any) => stockist.cluster_code === cluster_code);
        if (!stockistForCluster) {
          toast({
            description: `Stockist for cluster "${cluster_name}" is not selected.`,
            variant: "destructive",
          });
          return;
        }
      }
    }

    const groupedOrderGroups: any = {};
    Object.values(cartDataObj)?.forEach((item: any, index: number) => {
      const ordergroup_id = item?.companyProducts?.item_division[0]?.ordergroup_id?.ordergroup_id;
      const cluster_code = item?.companyProducts?.item_division[0]?.cluster_code?.cluster_code;
      const cmp_countertag_id = item?.counter_ff_div_id?.cmp_countertag_id;

      let cndnRemarksCode: string = "";
      if (cmp_countertag_id) {
        cndnRemarksCode = item?.counter_ff_div_id?.cmp_countertag?.cndn_remark_code;
      } else {
        cndnRemarksCode = item?.companyProducts?.item_division[0]?.ordergroup_id?.order_cndn_remarks?.cndn_remarks_code;
      }

      const groupKey = cmp_countertag_id ? `${cmp_countertag_id}_${cluster_code}` : `${ordergroup_id}_${cluster_code}`;

      // 🔁 Get stockist based on isSalesRequired
      let stockistForCluster;

      if (isSalesRequired) {
        const divisionCode = item?.counter_ff_div_id?.company_division_code?.company_divisioncode;
        const clusterDivisionKey = `${cluster_code}-${divisionCode}`;

        const selectedStockistForClusterDivision = selectedStockistMap?.[clusterDivisionKey];

        // Fallback to default stockist with same cluster_code
        const defaultStockist = Object.values(selectedStockistMap).find(
          (stockist: any) => stockist?.cluster_code == cluster_code && stockist?.is_default
        );

        stockistForCluster = defaultStockist || selectedStockistForClusterDivision;
      } else {
        stockistForCluster = selectedStockist.find((stockist) => stockist.cluster_code === cluster_code);
      }

      // Only create group if stockist is found
      if (!groupedOrderGroups[groupKey] && stockistForCluster) {
        groupedOrderGroups[groupKey] = {
          stockist_id: stockistForCluster?.id,
          company_stockist_code: stockistForCluster?.company_stockist_code,
          medvol_stockist_code: stockistForCluster?.medvol_stockist_code,
          cluster_code: cluster_code,
          cluster_ucode: item?.companyProducts?.item_division[0]?.cluster_code?.Cluster_UniqueId,
          ordergroup_id: ordergroup_id,
          cmp_countertag_id: cmp_countertag_id,
          cndn_remarks_code: cndnRemarksCode,
          order_mode: Number(orderType),
          headquarter_code: item?.counter_ff_div_id?.position_code?.emp_position_devrel[0]?.headQuarter_code,
          headquarter_name: item?.counter_ff_div_id?.position_code?.emp_position_devrel[0]?.headQuarter_name,
          bill_amount: subtotal,
          discount_amount: getTotalDiscount(),
          orderProducts: [],
        };
      }

      // Adjust quantity if needed
      let cartQty = item?.cartQty || 0;
      const priceIdentifierId = String(item?.priceldentifier?.priceldentifier_id ?? "");
      let qtyStep = 0;
      if (priceIdentifierId === "2") {
        qtyStep = Number(item?.counter_product_slab?.[0]?.schemeqty_cp || 0);
      } else if (priceIdentifierId === "1" || priceIdentifierId === "3") {
        qtyStep = Number(item?.companyProducts?.integral_quantity || 0); // round to nearest multiple of integral qty
      }
      if (qtyStep > 0 && cartQty % qtyStep !== 0) {
        cartQty = Math.ceil(cartQty / qtyStep) * qtyStep;
      }

      // Add product to order group
      if (groupedOrderGroups[groupKey]) {
        groupedOrderGroups[groupKey].orderProducts.push({
          counter_product: Number(item?.counter_product_lnk_id) || null,
          quantity: cartQty || 0,
          sequence_number: index + 1,
          ucode: generateUniqueID(),
          company_division_code: item?.counter_ff_div_id?.company_division_code?.company_divisioncode || "",
          item_division_id: item?.counter_ff_div_id?.company_division_code?.company_divisioncode || "",
          product_name: item?.companyProducts?.name || "",
          item_code: item?.companyProducts?.item_code || "",
          ptr: item?.companyProducts?.ptr || 0,
          // M10-13312
          batch_id: item?.batch_id || null,
          expiry_date: item?.expiry_date || null,
          display_type : item?.is_default_type || null,
          // M10-13312
        });
      }
    });

    const clusters_ordergroup_data = Object.values(groupedOrderGroups);

    const getCurrentLocation = (): Promise<{ latitude: number; longitude: number }> => {
      return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            },
            (error) => {
              reject(error);
            }
          );
        } else {
          reject(new Error("Geolocation is not supported by this browser."));
        }
      });
    };

    let latitude = 40.7128; // Default fallback
    let longitude = -74.006; // Default fallback

    // Fetch dynamic latitude and longitude
    try {
      const location = await getCurrentLocation();
      latitude = location.latitude;
      longitude = location.longitude;
    } catch (error) {
      console.log("Failed to get location, using default coordinates:", error);
      toast({ description: "Unable to fetch location. Using default coordinates.", variant: "destructive" });
    }

    const orderPayload = {
      data: {
        company_code: Number(selectedCounterData?.company_code?.code),
        counter_code: selectedCounterData?.counter_location?.counter_hdr_id?.counter_code,
        counter_ucode: selectedCounterData?.counter_location?.counter_hdr_id?.uid,
        counter_company_location_id: selectedCounterData?.counter_company_location_id,
        created_by: idTokenDetails?.sub || "",
        created_date: istISOString,
        source: "Web",
        latitude: latitude,
        longitude: longitude,
        actor_name: idTokenDetails?.name || "",
        actor_code: currentPsr?.emp_position_hdr?.position_code || "",
        actor_type: idTokenDetails?.["custom:role"],
        user_ucode: idTokenDetails?.["custom:ucode"] || "",
        cash_discount: 0,
        ucode: generateUniqueID(),
        order_freetxt_remarks: remark,
        remarkby_type: "MV",
        // position_code: currentPsr?.emp_position_hdr?.position_code || "9214LUPEDPXHYJ",
        location_code: selectedCounterData?.counter_location?.location_code,
        stockist_copy_path: filePath?.stockist_copy_path,
        counter_copy_path: filePath?.counter_copy_path,
        clusters_ordergroup: clusters_ordergroup_data,
        is_order_email_disabled: skipNotifications,
        // M10-16361: selected date + current IST time of placing the order, e.g. 2026-10-28T14:35:12.123+05:30
        // No date selected -> same as order initiated date (created_date)
        ...(canSetOrderCompleteDate ? { order_complete_date: `${orderedDate || todayIST}${istISOString.slice(10)}+05:30` } : {}),
      },
      event_type: "ORDER_ADD",
    };
    const selectedStockistIds: any = [];
    orderPayload?.data?.clusters_ordergroup?.forEach((item: any) => {
      const stockist = selectedStockist?.find(
        (stockist: any) => stockist?.id === item?.stockist_id && stockist?.cluster_code === item?.cluster_code && stockist?.is_default === false
      );
      if (stockist && !selectedStockistIds.some((s: any) => s.id === stockist.id)) {
        selectedStockistIds.push(stockist);
      }
    });
    if (selectedStockistIds?.length > 0) {
      selectedStockistIds?.forEach(async (item: any) => {
        const payload = {
          data: {
            is_default: true,
            counter_hdr_id: selectedCounterData?.counter_location?.counter_hdr_id?.counter_id,
            counter_company_id: selectedCounterData?.counter_company_location_id,
            remarks: "12342312",
            location_id: selectedCounterData?.counter_location?.counter_location_id,
            cluster_code: item?.cluster_code,
            company_code: selectedCounterData?.company_code?.code,
            counter_code: selectedCounterData?.counter_location?.counter_hdr_id?.counter_code,
            stockist_cluster: item?.stockist_cluster,
            u_code: generateUniqueID(),
          },
          primary_key: selectedCounterData?.counter_location?.counter_hdr_id?.counter_id,
          event_type: "COUNTER_STOCKIST_ADD",
        };

        isRequestInProgress.current = true;
        try {
          const res = await addCounterStockist(payload);
          if (res?.status !== 200) throw new Error(res?.data?.message);
          if (res?.status === 200) {
            setPlacingOrder(true);

            try {
              const res = await postPlaceOrder(orderPayload, selectedCounterData?.company_code?.code);
              if (res?.status !== 201) throw new Error(res?.data?.message);
              //M10-15110
              if (isMountedRef.current) {
                dispatch(ordActions.setOrderDetails(res?.data?.data));
                navigate("/transactions/orders/confirmation");
              }
              //M10-15110
              setOrderType(null);
              setPlacingOrder(false);
              setSelectedStockist([]);
            } catch (error: any) {
              setPlacingOrder(false);
              console.log(error);
              toast({ description: error?.message || "Failed to place order", variant: "destructive" });
              return;
            }
          }
        } catch (error: any) {
          setPlacingOrder(false);
          console.log(error);
          toast({ description: error?.response?.data?.message || "Failed to add stockist", variant: "destructive" });
          return;
        } finally {
          isRequestInProgress.current = false;
        }
      });
    } else {
      isRequestInProgress.current = true;
      try {
        setPlacingOrder(true);
        const res = await postPlaceOrder(orderPayload, selectedCounterData?.company_code?.code);
        if (res?.status !== 201) throw new Error(res?.data?.message);
        //M10-15110
        if (isMountedRef.current) {
          dispatch(ordActions.setOrderDetails(res?.data?.data));
          navigate("/transactions/orders/confirmation");
        }
        //M10-15110
        setOrderType(null);
        setPlacingOrder(false);
        setSelectedStockist([]);
      } catch (error: any) {
        setPlacingOrder(false);
        console.log(error);
        toast({ description: error?.message || "Failed to place order", variant: "destructive" });
        return;
      } finally {
        isRequestInProgress.current = false;
        setPlacingOrder(false);
      }
    }
  }, 1000);

  const fetchStockist = async () => {
    const newClusterCodes: any[] = [];
    const newClusterDivisions: any[] = [];

    Object.values(cartDataObj)?.forEach((item: any) => {
      const clusterCode = item?.companyProducts?.item_division[0]?.cluster_code?.cluster_code;
      if (clusterCode && !clusterCodes.includes(clusterCode) && !newClusterCodes.includes(clusterCode)) {
        newClusterCodes.push(clusterCode);
      }
    });
    setClusterCodes(newClusterCodes);

    const counterProductDivision: string[] = [];
    Object.values(cartDataObj)?.forEach((item: any) => {
      const divisionCode = item?.counter_ff_div_id?.company_division_code?.company_divisioncode;
      if (divisionCode && !counterProductDivision.includes(divisionCode)) {
        counterProductDivision.push(divisionCode);
      }
    });

    if (isSalesRequired) {
      Object.values(cartDataObj)?.forEach((item: any) => {
        const clusterCode = item?.companyProducts?.item_division?.[0]?.cluster_code?.cluster_code;
        const divisionCode = item?.counter_ff_div_id?.company_division_code?.company_divisioncode;
        const cluster_division = clusterCode + "-" + divisionCode;
        if (cluster_division && !clusterDivision.includes(cluster_division) && !newClusterDivisions.includes(cluster_division)) {
          newClusterDivisions.push(cluster_division);
        }
      });
      setClusterDivision(newClusterDivisions);
    }

    // filterClusterProducts();
    try {
      const res = await getStockist(selectedCounterData?.counter_company_location_id, newClusterCodes, counterProductDivision.join(","));
      if (res?.status !== 200) throw new Error(res?.data?.error);

      setStockist(res?.data?.data);
      setDistrictStockist(
        res?.data?.districtData.map((item: any) => ({ ...item, unique_stockist_id: `district-${item?.cluster_code}-${item?.stockist_id}` }))
      );
      setNonDefaultCounterStocist(
        res?.data?.nonDefaultCounterStocist?.map((item: any) => ({
          ...item,
          unique_stockist_id: `counter-stockist-${item?.cluster_code}-${item?.stockist_id}`,
        }))
      );

      const preSelectStockist = res?.data?.data
        ?.filter((item: any) => item?.is_default)
        .map((item: any) => ({
          name: item?.owner_name,
          id: item?.stockist_id,
          company_stockist_code: item?.company_stockist_code,
          cluster_code: item?.cluster_code,
          counter_stockist_Lnk_Id: item?.counter_stockist_Lnk_Id,
          medvol_stockist_code: item?.medvol_stockist_code,
          name_id: `${item?.cluster_name} - ${item?.owner_name}`,
          is_default: item?.is_default,
          unique_stockist_id: `${item?.cluster_code}-${item?.stockist_id}`,
        }));

      setSelectedStockist(preSelectStockist);
      setSelectedStockistMap(preSelectStockist);

      const selectedStockistIds: string[] = [];
      const selectedStockistClusterCodes: string[] = [];
      preSelectStockist.forEach((item: any) => {
        selectedStockistIds.push(item?.unique_stockist_id);
        selectedStockistClusterCodes.push(item?.cluster_code);
      });

      // ✅ Disable stockists not selected
      setStockist((prev: any) => {
        return prev.map((item: any) => {
          if (selectedStockistClusterCodes.includes(item?.cluster_code) && !selectedStockistIds.includes(item?.unique_stockist_id)) {
            item.disabled = true;
          } else {
            item.disabled = false;
          }
          return item;
        });
      });
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Failed to fetch stockist", variant: "destructive" });
    }
  };

  const handleOrderTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderType(e.target.value);
  };

  useEffect(() => {
    if (cartDataObj) {
      const divisionsData = Object.values(cartDataObj)?.reduce((acc: any[], item: any) => {
        const division_code = item?.counter_ff_div_id?.company_division_code?.company_divisioncode;
        const orderValue = item?.cartQty * item?.counter_product_slab?.[0]?.price;
        const division_name = item?.counter_ff_div_id?.company_division_code?.division_name;

        // check if already present
        const existing = acc.find((d) => d.division_code === division_code);

        if (existing) {
          existing.orderValue += orderValue;
        } else {
          acc.push({
            division_code,
            orderValue,
            division_name,
          });
        }

        return acc;
      }, []);
      setDivisionsData(divisionsData);
    }
    fetchStockist();
  }, [locationCode]);

  return (
    <div>
      {checkingUINLoading && (
        <div className="inset-x-0 top-0 z-50 backdrop-blur-sm">
          <div className="flex items-center justify-center p-1 bg-gradient-to-r from-violet-600 via-purple-500 to-indigo-500">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full border-[3px] border-white/80 border-t-transparent animate-spin" />
              <p className="text-sm font-medium text-white/90">Checking UIN...</p>
            </div>
          </div>
        </div>
      )}

      <div>
        {isLoading ? (
          <div className="flex items-center justify-center h-screen">
            <Spinner />
          </div>
        ) : (
          <>
            {/* <div className="p-6 px-10 border-b border-purple-200 shadow-md bg-gradient-to-r from-purple-50 to-purple-100"> */}
            <div className="p-6 px-16 border-b shadow-md 2xl:px-16">
              <Button className="text-lg px-0 text-[#7F56D9]" onClick={() => navigate(`/transactions/orders/products/${locationCode}`)}>
                <img src={PurpleLeftArrowIcon} alt="back" className="mr-4" />
                Back to products
              </Button>
              <div className="w-full px-0">
                <h1 className="font-semibold text-[2rem]">Review Order</h1>
              </div>
            </div>
            {placingOrder ? (
              <div className="flex flex-col justify-center items-center h-[50vh]">
                <Spinner />
                <h1 className="text-[1.5rem] mt-4 font-semibold">Please Wait</h1>
                <p className="text-[1rem] text-gray-500">Your order is being placed.</p>
              </div>
            ) : (
              <div className="flex justify-center w-full gap-10 px-16 mt-0 2xl:px-16">
                <div className="mt-10 w-[70%]">
                  <div className="flex items-center justify-between mb-0">
                    <h1 className="font-semibold text-[1.4rem]">All Products</h1>
                    <p className="bg-[#F8F7FF] px-4 py-2 rounded-lg text-[#8051ff] font-medium text-[.9rem]">
                      {Object.keys(cartDataObj)?.length} Items | {totalItems} units
                    </p>
                  </div>

                  {isSalesRequired
                    ? clusterDivision?.map((item: any) => {
                      const iSalesDivisionProductCodes = [
                        ...new Set(
                          Object.values(cartDataObj)
                            .filter((product: any) => product?.counter_ff_div_id?.cmp_countertag?.is_division_order_required)
                            .map((product: any) => product?.counter_ff_div_id?.company_division_code?.company_divisioncode)
                        ),
                      ];
                      const clusterProducts = Object.values(cartDataObj)?.filter((product: any) => {
                        const clusterCode = product?.companyProducts?.item_division[0]?.cluster_code?.cluster_code;
                        const divisionCode = product?.counter_ff_div_id?.company_division_code?.company_divisioncode;
                        const cluster_division = clusterCode + "-" + divisionCode;
                        return cluster_division === item;
                      });
                      const clusterStockist = stockist?.filter((stockist: any) => stockist?.cluster_code == item?.split("-")[0]?.trim());
                      const selectedStockists = selectedStockist?.filter((stockist: any) => stockist?.cluster_code == item?.split("-")[0]?.trim());
                      const productDivisionCode = item?.split("-")[1]?.trim();
                      // Filter district stockist if no default exists
                      const hasDefault = clusterStockist?.some((s: any) => s.is_default);
                      const filteredClusterStockist = hasDefault
                        ? clusterStockist.filter((stockist: any) => stockist.is_default)
                        : iSalesDivisionProductCodes.includes(Number(productDivisionCode))
                          ? [
                            ...(nonDefaultCounterStocist
                              ?.filter((stockist: any) => stockist?.cluster_code == item?.split("-")[0])
                              .map((stockist: any) => ({
                                ...stockist,
                                unique_stockist_id: `counter-stockist-${stockist?.cluster_code}-${stockist?.stockist_id}-${productDivisionCode}`,
                                is_district: true,
                              })) || []),
                          ]
                          : [
                            ...(districtStockist
                              ?.filter((district: any) => district?.cluster_code == item?.split("-")[0])
                              .map((district: any) => ({
                                ...district,
                                unique_stockist_id: `district-${district?.cluster_code}-${district?.stockist_id}-${productDivisionCode}`,
                                is_district: true,
                              })) || []),
                            ...(nonDefaultCounterStocist
                              ?.filter((stockist: any) => stockist?.cluster_code == item?.split("-")[0])
                              .map((stockist: any) => ({
                                ...stockist,
                                unique_stockist_id: `counter-stockist-${stockist?.cluster_code}-${stockist?.stockist_id}-${productDivisionCode}`,
                                is_district: true,
                              })) || []),
                          ];

                      return (
                        <OrderReviewProductCard
                          key={item}
                          clusterProducts={clusterProducts}
                          clusterStockist={filteredClusterStockist}
                          selectedStockist={
                            isSalesRequired
                              ? [
                                selectedStockistMap[item] ||
                                Object.values(selectedStockistMap).find(
                                  (s: any) => s?.is_default && String(s?.cluster_code) === item?.split("-")[0]
                                ),
                              ].filter(Boolean)
                              : selectedStockists
                          }
                          handleStockistChange={
                            isSalesRequired ? (value, type) => handleClusterDivisionStockistChange(value, type, item) : handleStockistChange
                          }
                        />
                      );
                    })
                    : clusterCodes?.map((item: any) => {
                      const clusterProducts = Object.values(cartDataObj)?.filter(
                        (product: any) => product?.companyProducts?.item_division[0]?.cluster_code?.cluster_code === item
                      );
                      const clusterStockist = stockist?.filter((stockist: any) => stockist?.cluster_code === item);
                      const selectedStockists = selectedStockist?.filter((stockist: any) => stockist?.cluster_code === item);

                      // Filter district stockist if no default exists
                      const hasDefault = clusterStockist?.some((s: any) => s.is_default);
                      const filteredClusterStockist = hasDefault
                        ? clusterStockist.filter((stockist: any) => stockist.is_default)
                        : [
                          ...(districtStockist
                            ?.filter((district: any) => district?.cluster_code === item)
                            .map((district: any) => ({
                              ...district,
                              unique_stockist_id: `district-${district?.cluster_code}-${district?.stockist_id}`,
                              is_district: true,
                            })) || []),
                          ...(nonDefaultCounterStocist
                            ?.filter((stockist: any) => stockist?.cluster_code == item)
                            .map((stockist: any) => ({
                              ...stockist,
                              unique_stockist_id: `counter-stockist-${stockist?.cluster_code}-${stockist?.stockist_id}`,
                              is_district: true,
                            })) || []),
                        ];
                      return (
                        <OrderReviewProductCard
                          key={item}
                          clusterProducts={clusterProducts}
                          clusterStockist={filteredClusterStockist}
                          selectedStockist={selectedStockists}
                          handleStockistChange={handleStockistChange}
                        />
                      );
                    })}

                  <div className="flex justify-end mt-4 mb-20">
                    <div className="flex flex-col justify-start gap-4">
                      {/* Order Mode */}
                      <div className="flex items-center">
                        <p className="text-[1rem] text-gray-500 w-40">Order Mode :</p>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="orderType"
                              value="10"
                              checked={orderType === "10"}
                              onChange={handleOrderTypeChange}
                              className="text-indigo-500 form-radio"
                            />
                            <span>MV MO with Approval</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="orderType"
                              value="11"
                              checked={orderType === "11"}
                              onChange={handleOrderTypeChange}
                              className="text-indigo-500 form-radio"
                            />
                            <span>MV MO without Approval</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex items-start gap-16">
                        {/* Order Placed Date - M10-16361 */}
                        {canSetOrderCompleteDate && (
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              <p className="text-[1rem] text-gray-500 w-40">Order Placed Date:</p>
                              <input
                                type="date"
                                value={orderedDate}
                                max={todayIST}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow clearing; block future dates typed manually
                                  if (!value || value <= todayIST) setOrderedDate(value);
                                }}
                                className="border border-gray-300 rounded-md px-3 py-1 text-[1rem]"
                              />
                            </div>
                            {orderedDate === todayIST && (
                              <p className="text-[0.9rem] text-amber-600 mt-2 max-w-[36rem]">
                                You are trying to select today's date as order placed date, so order initiated date and order placed date will be
                                the same.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Skip Email/SMS */}
                        {hasSkipNotificationsPermission && (
                          <div className="flex items-center">
                            <p className="text-[1rem] text-gray-500 w-40">Skip Email/SMS & Hide Order:</p>
                            <Checkbox
                              checked={skipNotifications}
                              onChange={(data: any) => {
                                setSkipNotifications(data.target.checked);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-[25%] mt-10 bg-white">
                  {/* Remark Section */}
                  <div className="mb-6">
                    <label className="block mb-4 text-[1.4rem] font-semibold">Add remarks</label>
                    {
                      <div>
                        <div className="flex flex-col gap-2 mb-10">
                          <h1 className="font-semibold text-gray-600">Customer Upload</h1>
                          <form onSubmit={handleUpload} className="flex justify-between gap-2">
                            <label className="relative px-4 py-2 font-semibold text-white bg-indigo-500 rounded-lg cursor-pointer hover:bg-indigo-600">
                              Choose File
                              <input
                                type="file"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={handleFileChange}
                                accept="image/*,.pdf,.doc,.docx,.xlsx,.ppt,.pptx,.txt"
                              />
                            </label>
                            <div className="flex items-center gap-2">
                              {file ? (file.name.length > 7 ? `${file.name.slice(0, 10)}...` : file.name) : "No file chosen"}
                              {file && (
                                <button type="button" onClick={() => setFile(null)} className="p-1 text-gray-500 hover:text-red-500">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                      fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              )}
                              {filePath?.counter_copy_path && (
                                <span className="flex">
                                  <img src={SuccessIcon} alt="file" className="w-6" />
                                </span>
                              )}
                            </div>
                            {/* <Button
                              type="submit"
                              disabled={uploading}
                              className="px-4 py-2 font-semibold text-white bg-indigo-500 rounded-lg hover:bg-indigo-600"
                            >
                              {uploading ? "Uploading..." : "Upload"}
                            </Button> */}
                          </form>
                        </div>
                        <div className="flex flex-col gap-2 mb-10 ">
                          <h1 className="font-semibold text-gray-600">Stockist Upload</h1>
                          <form onSubmit={handleUpload} className="flex justify-between gap-2">
                            <label className="relative px-4 py-2 font-semibold text-white bg-indigo-500 rounded-lg cursor-pointer hover:bg-indigo-600">
                              Choose File
                              <input
                                type="file"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={handleStockistFileChange}
                                accept="image/*,.pdf,.doc,.docx,.xlsx,.ppt,.pptx,.txt"
                              />
                            </label>
                            <div className="flex items-center gap-2">
                              {stockistFile
                                ? stockistFile.name.length > 7
                                  ? `${stockistFile.name.slice(0, 10)}...`
                                  : stockistFile.name
                                : "No file chosen"}
                              {stockistFile && (
                                <button type="button" onClick={() => setStockistFile(null)} className="p-1 text-gray-500 hover:text-red-500">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                      fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              )}
                              {filePath?.stockist_copy_path && (
                                <span className="flex">
                                  <img src={SuccessIcon} alt="file" className="w-6" />
                                </span>
                              )}
                            </div>
                          </form>
                          <Button
                            type="submit"
                            onClick={() => {
                              handleUpload();
                            }}
                            disabled={uploading}
                            loaderSize="small"
                            isLoading={uploading}
                            className="px-4 mt-4 py-2 font-semibold text-white bg-indigo-500 rounded-lg hover:bg-indigo-600"
                          >
                            {uploading ? "Uploading..." : "Upload"}
                          </Button>
                        </div>
                      </div>
                    }
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Type your remark here..."
                        value={remark}
                        onChange={handleRemarkChange}
                        className="flex-grow px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Pricing Summary */}
                  <div className="mb-6 space-y-3">
                    <div className="flex justify-between mb-2 text-lg">
                      <span>Subtotal</span>
                      <span className="">₹{subtotal?.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total</span>
                      {/* <span>{(subtotal - getTotalDiscount())?.toFixed(2)}</span> */}
                      <span>₹{subtotal?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Place Order Button */}
                  <Button
                    isLoading={isPlacingOrder}
                    disabled={isPlacingOrder}
                    loaderSize="small"
                    onClick={() => handlePlaceOrderButtonClick(false, false)}
                    className="w-full py-2 font-semibold text-white bg-indigo-500 rounded-lg hover:bg-indigo-600"
                  >
                    Place Order
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {checkingUINOpen ? modal : null}
      <FormSuccess
        onCloseModal={() => {
          setIsNonGSTOrderLimitModalOpen(false);
        }}
        showForm={isNonGSTOrderLimitModalOpen}
        onContinue={() => {
          setIsNonGSTOrderLimitModalOpen(false);
          // checkUINValidation()
          handlePlaceOrderButtonClick(true, false);
        }}
        enableBackButton={true}
        variant="error"
        popupMessage={{
          description: gstLimitExceededMessage,
          title: "Non-GST Order Limit!",
        }}
      />

      <FormSuccess
        showForm={hqValueValidationModalOpen}
        onContinue={() => {
          setHqValueValidationModalOpen(false)
          setHqValueValidationRes(null)
        }}
        enableBackButton={false}
        variant="error"
        popupMessage={{
          description:buildHqValidationDescriptionString(hqValueValidationRes),
          title: "Value Limit Alert!",
        }}
        buttonDescription="Close"
      />
    </div>
  );
};

export default OrderReviewScreen;
